import { useEffect, useState } from 'react'
import { usePaymentResultMercure } from './usePaymentResultMercure'
import { usePaymentResultPolling } from './usePaymentResultPolling'
import { createLogger } from '../utils/logger'

const sseConnectTimeoutMs = 5000

const logger = createLogger('usePaymentResultRealtime')

const isFinal = (status?: string) => status === 'success' || status === 'failed'

export const usePaymentResultRealtime = (
    paymentPageId: string,
    transactionId: number,
    enabled: boolean = true,
) => {
    useEffect(() => {
        logger.debug(`Realtime ${enabled ? 'initialized' : 'disabled'} for payment ${paymentPageId}:${transactionId}`)
    }, [paymentPageId, transactionId, enabled])

    const [sseTimedOut, setSseTimedOut] = useState(false)

    const {
        paymentResult: sseResult,
        error: sseError,
        connected,
    } = usePaymentResultMercure(paymentPageId, transactionId, enabled)

    useEffect(() => {
        if (!enabled) {
            return
        }

        if (connected) {
            logger.info('SSE connected successfully, disabling polling fallback')

            setSseTimedOut(false)
        } else {
            // Даем время на подключение или переподключение при потере соединения (например, при неактивной вкладке)
            const reason = sseError ? 'SSE error detected' : 'Initial SSE connection'
            logger.debug(`${reason}, waiting ${sseConnectTimeoutMs}ms before activating polling fallback`)

            const t = setTimeout(() => {
                logger.warn('SSE connection timeout, enabling polling fallback')

                setSseTimedOut(true)
            }, sseConnectTimeoutMs)

            return () => clearTimeout(t)
        }
    }, [connected, sseError, enabled])

    const shouldPolling = enabled && sseTimedOut && !isFinal(sseResult?.status)

    useEffect(() => {
        logger.debug(shouldPolling ? 'Polling fallback needed' : 'Polling fallback needless')
    }, [shouldPolling])

    const {
        paymentResult: polledResult,
        loading: polledLoading,
        error: polledError,
    } = usePaymentResultPolling(paymentPageId, transactionId, shouldPolling)

    const paymentResult = sseResult ?? polledResult
    const loading = enabled && !paymentResult && (connected || polledLoading)
    const error = !paymentResult && sseError && polledError

    useEffect(() => {
        if (paymentResult) {
            logger.info(`Payment result received: status=${paymentResult.status}`)
        }
    }, [paymentResult])

    return {
        paymentResult,
        loading,
        error,
    }
}
